import type { UIRouterReact } from "@uirouter/react/lib/core";

import Sentry from "@similarweb/sentry-logger";

import { applyCurrentPage, setIsPageTransitioning } from "app/actions/routingActions";
import { chosenItems } from "app/common/services/chosenItems";
import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    EnumWizardItems,
    setActivationWizardItems,
} from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardApi";
import { organicLandingPagesValidator } from "app/pages/keyword-research/website-analysis/pages/landing-pages/stateValidate";
import { RecentService } from "app/services/recent/recentService";
import { SwRedux } from "app/services/SwRedux";
import { SwTrack } from "app/services/SwTrack";
import { allTrackers } from "app/services/track/track";
import {
    beginPageViewTrackingInvocation,
    endPageViewTrackingInvocation,
} from "app/services/track/pageViewTrackSignal";
import {
    getPageViewDropContextFromState,
    reportDroppedPageView,
    shouldReportPageViewForState,
} from "app/services/track/reportDroppedPageView";
import { isNoTouch, isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import type { IRouterState, IRouterStateWithName } from "single-spa/react-app/router/IRouterState";
import { resolveRouterTrackingId } from "single-spa/react-app/router/resolveRouterTrackingId";

import { proNavigator } from "../ProNavigator";

const i18n = i18nFilter();

interface IWindow extends Window {
    proLoadingStart?: number;
}
declare const window: IWindow;

function hasDefaultQueryParams(toState: any, toParams: any): boolean {
    // checks if a url's query params contains a state's default query params
    if (toState.defaultQueryParams) {
        let returnFlag = true;
        for (const key in toState.defaultQueryParams) {
            if (!(key in toParams)) {
                returnFlag = false;
                break;
            }
        }
        return returnFlag;
    }
    return true;
}

const PAGE_VIEW_TRACKING_TIMEOUT_MS = 10_000;

// React Query pauses (does not reject) fetches started while offline, so an awaited
// handler can stay pending indefinitely and hold the invocation open.
const settledWithinTimeout = async (promise: PromiseLike<unknown>, ms: number): Promise<boolean> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        return await Promise.race([
            Promise.resolve(promise).then(() => true),
            new Promise<false>((resolve) => {
                timer = setTimeout(() => resolve(false), ms);
            }),
        ]);
    } finally {
        clearTimeout(timer);
    }
};

export const routerOnSuccess = (router: UIRouterReact) => {
    const swRedux = SwRedux.getInstance();
    router.transitionService.onSuccess({}, async (transition) => {
        // `name` is narrowed to `string`: the router guarantees a registered state name on transition.to()
        const toState = transition.to() as IRouterStateWithName;
        const toParams = transition.params();
        const fromState = transition.from() as IRouterState;

        // Set Sentry context from route's pageId/trackingId
        const trackingId = resolveRouterTrackingId(toState, toParams);
        if (trackingId) {
            Sentry.setTag("page.section", trackingId.section);
            if (trackingId.subSection) {
                Sentry.setTag("page.subSection", trackingId.subSection);
            }
            if (trackingId.subSubSection) {
                Sentry.setTag("page.subSubSection", trackingId.subSubSection);
            }
        }

        const isTrial = isTrialOrAfterTrial();
        const isNT = isNoTouch();
        if (isTrial || isNT) {
            if (
                (fromState.name === "dashboard-gallery" && toState.name !== "dashboard-gallery-blank") ||
                (fromState.name === "dashboard-gallery-blank" && toState.name !== "dashboard-gallery")
            ) {
                setActivationWizardItems(EnumWizardItems.IsCreateDashboardFromHP, false);
            }
        }
        swRedux.dispatch(setIsPageTransitioning(true));
        setTimeout(() => {
            // After navChangeSuccess completes (using timeout), if page is still in transition and
            // didn't yet apply current page (by controller decorator), then apply current page.
            if (swRedux.getState().routing.isPageTransitioning) {
                swRedux.dispatch(applyCurrentPage());
            }
        });

        const rawHash = window.location.hash;
        // Extract the part after the LAST "#", which is your element id
        const id = rawHash.includes("#") ? rawHash.split("#").pop() : "";
        if (id) {
            // Wait for the view to render, then scroll
            setTimeout(() => {
                document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                // remove the last hash from the url
                window.history.replaceState({}, "", window.location.pathname.split("#").slice(0, -1).join("#"));
            }, 100);
        }

        if (toState.name === "insights-reports" && fromState.name === "insightsHome" && toParams.types) {
            const groupNames = Object.keys(swSettings.components.DeepInsightsGroups.resources);
            let type = null;
            let tile: { Name: string } | null = null;
            groupNames.forEach((gr) => {
                const keys = Object.keys(swSettings.components[gr].resources);
                const items = keys.filter((t) => t === "ReportType_" + toParams.types);
                if (items.length) {
                    type = swSettings.components.DeepInsightsGroups.resources[gr];
                    const reportType = swSettings.components[gr].resources["ReportType_" + toParams.types];
                    tile = JSON.parse(reportType.replace(new RegExp("'", "gi"), '"'));
                    return false;
                }
            });

            const typeLabel = type || toParams.types;
            // @ts-ignore FIXME: strict migration - 'tile' is possibly 'null' — set inside the forEach when items match; preserved legacy assumption that this branch only runs when tile is found
            SwTrack.all.trackEvent("Deep Insights Solutions", "click", `${typeLabel}/${tile.Name}`);
        }

        if (toState.pageTitle) {
            if (typeof toState.pageTitle === "function") {
                document.title = i18n(toState.pageTitle(toParams));
            } else {
                document.title = i18n(toState.pageTitle);
            }
        }

        setTimeout(async () => {
            try {
                if (typeof toState.data.pageViewTracking === "function") {
                    const invocation = beginPageViewTrackingInvocation();
                    let timedOut = false;
                    try {
                        const handlerResult = toState.data.pageViewTracking.apply(toState.data, [
                            {
                                toState,
                                toParams: { ...toParams, notPermitted: true },
                            },
                        ]);
                        // Only genuinely async handlers yield; sync ones stay atomic within this macrotask.
                        if (typeof handlerResult?.then === "function") {
                            timedOut = !(await settledWithinTimeout(handlerResult, PAGE_VIEW_TRACKING_TIMEOUT_MS));
                        }
                    } finally {
                        const { tracked, skipped, superseded } = endPageViewTrackingInvocation(invocation);
                        if (!superseded && !tracked && !skipped && shouldReportPageViewForState(toState)) {
                            reportDroppedPageView({
                                reason: timedOut ? "handler_timeout" : "handler_did_not_track",
                                ...getPageViewDropContextFromState(toState, toParams),
                            });
                        }
                    }
                } else if (shouldReportPageViewForState(toState)) {
                    reportDroppedPageView({
                        reason: "missing_or_null_config",
                        pageViewTrackingType: typeof toState.data?.pageViewTracking,
                        ...getPageViewDropContextFromState(toState, toParams),
                    });
                }
            } catch (e) {
                Sentry.error("Error invoking pageViewTracking - router-on-success", e);
            }
        }, 100);

        await RecentService.addCurrentPageToRecent();

        SwTrack.setRecordingStatus(!toState.data.disableRecording);

        // apps title is updated in the appInfoResolver because app name is not known from the toParams
        if (toState.name.indexOf("keywords") > -1 && !toState.parent?.includes("keywordsCampaign")) {
            const title = i18n("titleTag.keywords");
            document.title = title;
        }
        if (
            toState.name === "organicsearch_website_landingpages_v2" ||
            toState.name === "organicsearch_website_organiclandingpages_v2"
        ) {
            const isValidState = organicLandingPagesValidator(toParams);
            if (!isValidState) {
                return transition.router.stateService.target(toState, { selectedDomain: null });
            }
        }

        const isWebsiteState =
            toState.name.match(/websites[-_]/) ||
            toState.name.match(/accountreview_website_/) ||
            toState.name.match(/digitalsuite_website_/) ||
            toState.name.match(/organicsearch_website_/) ||
            toState.name.match(/paidsearchsearch_website_/) ||
            toState.name.match(/organicsearch_websites_/) ||
            toState.name.match(/searchadvertising/);

        const isMobileState =
            (toState.name.match(/apps-/) || toState.name.match(/_app_/) || toState.name.match(/keywords-/)) &&
            !toState.name.match(/-home/);

        if (isWebsiteState) {
            // Clear universal search on web analysis
            chosenItems.$clear();
        }

        if (isMobileState) {
            // clear universal search for mobile apps
            chosenSites.clear(toParams);
        }

        // Clear universal search on homepage and dashboard
        if (toState.clearSearch) {
            chosenItems.$clear();
            chosenSites.clear(toParams);
        }

        // update config object according to state's configId
        const component = proNavigator.getConfigIdFromStateConfig(toState, toParams);
        if (component) {
            await swSettings.setCurrent([component]);
            // Temp comment - shut down sentry performance activity
            // navigationSpan.setStatus(SpanStatus.Ok);
        }
        // Temp comment - shut down sentry performance activity
        // closeNavigationTransaction();

        const { proLoadingStart } = window;

        if (proLoadingStart) {
            delete window.proLoadingStart;
            if (!swSettings.user?.impersonateSource) {
                const now = performance.now();
                const proLoadingTimeInSeconds = (now - proLoadingStart) / 1000;
                const message = "Pro successfully loaded, first navigation finished successfully";
                const PrefersDarkColorScheme = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
                allTrackers.dispatchTrackEvent("Loading", "Loaded", message, {
                    "Loading Time In Seconds": proLoadingTimeInSeconds,
                    "Prefers Dark Color Scheme": PrefersDarkColorScheme,
                    "State Name": toState.name,
                });
            }
        }
    });
};
